// #region Imports
import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchInboundSla,
    patchInboundOverride,
    resetInboundSla,
    resetPatchInboundOverrideStatus,
    selectInboundSlaData,
    selectInboundSlaStatus,
    selectInboundSlaError,
    selectPatchInboundOverrideStatus,
} from 'redux/slices/sla-reports/inbound-sla';
import { setToast, setShowToast } from '_redux/slices/timeline/timeline-toast';
import InboundSlaSummary from './inbound-sla-summary';
import InboundSlaRawDataTable from './inbound-sla-raw-data-table';
import InboundSlaIgnoredPanel from './inbound-sla-ignored-panel';
import ReportHeader from './report-header';
import { generateRequestId } from '_services/queue-status-service';
import QueueStatusBanner from '_atoms/queue-status-banner';
import SlaReasonSummaryPanel, { reasonSummaryFromReports } from './sla-reason-summary';
// #endregion

const InboundSlaReport = ({ dateRange, iata, runReportRef, onRunLoadingChange, hideBreakdown }) => {
    const [overrideContext, setOverrideContext] = useState(null);
    const [includeNoData, setIncludeNoData] = useState(false);
    const [includeReasonSummary, setIncludeReasonSummary] = useState(false);
    const [activeRequestId, setActiveRequestId] = useState(null);

    const dispatch = useDispatch();
    const data = useSelector(selectInboundSlaData);
    const status = useSelector(selectInboundSlaStatus);
    const error = useSelector(selectInboundSlaError);
    const patchStatus = useSelector(selectPatchInboundOverrideStatus);
    const isLoading = status === 'loading';
    const isPatching = patchStatus === 'loading';

    const handleRunReport = useCallback(() => {
        const startDate = dateRange?.[0];
        const endDate = dateRange?.[1];

        if (!startDate || !endDate || !iata) return;
        if (isLoading) return; // a report is already running — don't stack duplicate requests

        const reqId = generateRequestId();
        setActiveRequestId(reqId);

        const toLocalDateString = (d) => { const dt = new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; };
        const formattedStartDate = toLocalDateString(startDate);
        const formattedEndDate = toLocalDateString(endDate);

        dispatch(
            fetchInboundSla({
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                iata,
                hideBreakdown,
                requestId: reqId,
            })
        );
    }, [dispatch, dateRange, iata, hideBreakdown, isLoading]);

    useEffect(() => {
        return () => {
            dispatch(resetInboundSla());
        };
    }, [dispatch]);

        useEffect(() => {
        if (patchStatus === 'succeeded') {
            // The override was applied to the loaded report optimistically in the
            // slice, so we don't refetch the (30s+) report here — just confirm.
            dispatch(setToast({ message: 'Booking updated successfully', statusType: 'success' }));
            dispatch(setShowToast(true));
            dispatch(resetPatchInboundOverrideStatus());
            setOverrideContext(null);
        }
    }, [patchStatus, dispatch]);

        useEffect(() => {
        if (patchStatus === 'failed') {
            dispatch(setToast({ message: 'Failed to update booking', statusType: 'failed' }));
            dispatch(setShowToast(true));
            dispatch(resetPatchInboundOverrideStatus());
            // The optimistic local change didn't persist — resync from the server
            // so the on-screen numbers match what was actually saved.
            const toLocalDateString = (d) => { const dt = new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; };
            const startDate = dateRange?.[0] ? toLocalDateString(dateRange[0]) : null;
            const endDate = dateRange?.[1] ? toLocalDateString(dateRange[1]) : null;
            if (startDate && endDate && iata) {
                dispatch(fetchInboundSla({ startDate, endDate, iata, hideBreakdown }));
            }
        }
    }, [patchStatus, dispatch, dateRange, iata, hideBreakdown]);

    useEffect(() => {
        if (runReportRef) runReportRef.current = handleRunReport;
        return () => { if (runReportRef) runReportRef.current = null; };
    }, [handleRunReport, runReportRef]);

    useEffect(() => {
        if (onRunLoadingChange) onRunLoadingChange(isLoading);
        if (!isLoading) setActiveRequestId(null);
    }, [isLoading, onRunLoadingChange]);

    const dailyReports = data?.dailyReports ?? [];
    const isMultiDay = (data?.dateRange?.length ?? 0) > 1;

    return (
        <div>
            <ReportHeader
                onRunReport={handleRunReport}
                isLoading={isLoading}
                hideRunButton={!!runReportRef}
                checkboxes={[
                    {
                        label: 'Include override and pass summary',
                        checked: includeReasonSummary,
                        onChange: (e) => setIncludeReasonSummary(e.target.checked),
                    },
                ]}
            />

            <QueueStatusBanner requestId={activeRequestId} />

            {status === 'failed' && (
                <p style={{ color: '#991b1b', fontSize: '13px' }}>Error loading Inbound SLA report: {error}</p>
            )}

            {data && (
                <>
                    <InboundSlaSummary
                        dailyReports={dailyReports}
                        overallSummary={isMultiDay ? data.summary : null}
                        includeNoData={includeNoData}
                    />
                    {includeReasonSummary && (
                        <SlaReasonSummaryPanel summary={reasonSummaryFromReports(data.reasonSummary, dailyReports)} />
                    )}
                    <InboundSlaIgnoredPanel
                        ignoredBookings={data.ignoredBookings ?? []}
                        dailyReports={dailyReports}
                        hideBreakdown={hideBreakdown}
                    />
                    {!hideBreakdown && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '24px', marginBottom: '12px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#4b5563', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={includeNoData}
                                        onChange={(e) => setIncludeNoData(e.target.checked)}
                                        style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#3b82f6' }}
                                    />
                                    Include no-data (skip) rows
                                </label>
                            </div>
                            <InboundSlaRawDataTable
                                dailyReports={dailyReports}
                                overrideContext={overrideContext}
                                onOverrideRowChange={setOverrideContext}
                                onPatchInbound={(row, pass, reason, { rows } = {}) => {
                                    const bookingIds = [...new Set((rows?.length ? rows : [row]).map((r) => r?.bookingId).filter(Boolean))];
                                    if (bookingIds.length && overrideContext?.kpiKey && reason) {
                                        dispatch(patchInboundOverride({
                                            bookingId: bookingIds[0],
                                            bookingIds,
                                            kpiKey: overrideContext.kpiKey,
                                            pass,
                                            passReason: reason.passReason,
                                            reasonCode: reason.reasonCode,
                                            reasonDetail: reason.reasonDetail,
                                        }));
                                    }
                                }}
                                isPatching={isPatching}
                                includeNoData={includeNoData}
                            />
                        </>
                    )}
                </>
            )}

        </div>
    );
};

export default InboundSlaReport;
